import { useEffect, useRef, useState, useMemo } from "react";
import Globe from "react-globe.gl";

const TOOLTIP_OFFSET_X = 30;
const TOOLTIP_OFFSET_Y = -20;

const TooltipContent = ({ city, renderSeverityItems }) => (
  <div
    className={[
      "min-w-[200px] overflow-hidden rounded-lg",
      "bg-white/95 dark:bg-[#0B0F14]/95",
      "border border-slate-200/70 dark:border-[#0EA5E9]/20",
      "shadow-xl backdrop-blur-xl",
      "font-sans",
    ].join(" ")}
  >
    <div className="p-3">
      <div className="text-slate-800 dark:text-gray-100 mb-0.5 text-sm">
        {city.city}
      </div>
      <div className="text-slate-500 dark:text-gray-400 text-xs">
        {city.country}
      </div>
    </div>
    <div className="px-3 py-2 border-t border-b border-slate-200 dark:border-[#0EA5E9]/20 bg-slate-50/50 dark:bg-gray-900/30">
      <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider">
        Relevance Breakdown
      </div>
    </div>
    <div className="flex flex-col">{renderSeverityItems(city)}</div>
  </div>
);

export function GlobeVisualization({
  data,
  onPointClick,
  currentPoint,
  darkmode,
}) {
  const globeEl = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedCity, setSelectedCity] = useState(null);
  const [hoveredCity, setHoveredCity] = useState(null);
  const [passiveCity, setPassiveCity] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [hoverTooltipPos, setHoverTooltipPos] = useState({ x: 0, y: 0 });
  const [passiveTooltipPos, setPassiveTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedVisible, setSelectedVisible] = useState(false);
  const [hoveredVisible, setHoveredVisible] = useState(false);
  const [passiveVisible, setPassiveVisible] = useState(false);
  const hasInitialized = useRef(false);
  const userHasInteracted = useRef(false);

  useEffect(() => {
    const container = document.getElementById("globe-container");
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        setDimensions({ width, height });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const aggregatedCities = useMemo(() => {
    const cityMap = new Map();

    data.forEach((point) => {
      const cityName = point.loc_data?.city || "Unknown";
      const country = point.country || "";
      const key = `${cityName}-${country}-${point.lat.toFixed(2)}-${point.lon.toFixed(2)}`;

      if (!cityMap.has(key)) {
        cityMap.set(key, {
          id: key,
          city: cityName,
          country: country,
          lat: point.lat,
          lon: point.lon,
          points: [],
          high: 0,
          medium: 0,
          low: 0,
          total: 0,
        });
      }

      const cityData = cityMap.get(key);
      cityData.points.push(point);
      cityData.total += 1;

      if (point.severity === "high") cityData.high += 1;
      else if (point.severity === "medium") cityData.medium += 1;
      else if (point.severity === "low") cityData.low += 1;
    });

    return Array.from(cityMap.values());
  }, [data]);

  const mostConcentratedArea = useMemo(() => {
    if (aggregatedCities.length === 0) return null;

    const gridSize = 30;
    const grid = new Map();

    aggregatedCities.forEach((city) => {
      const latBucket = Math.floor(city.lat / gridSize) * gridSize;
      const lonBucket = Math.floor(city.lon / gridSize) * gridSize;
      const key = `${latBucket},${lonBucket}`;

      if (!grid.has(key)) {
        grid.set(key, { lat: 0, lon: 0, total: 0, count: 0 });
      }

      const bucket = grid.get(key);
      bucket.lat += city.lat * city.total;
      bucket.lon += city.lon * city.total;
      bucket.total += city.total;
      bucket.count += 1;
    });

    let bestBucket = null;
    let bestTotal = 0;

    grid.forEach((bucket) => {
      if (bucket.total > bestTotal) {
        bestTotal = bucket.total;
        bestBucket = bucket;
      }
    });

    if (bestBucket?.total > 0) {
      return {
        lat: bestBucket.lat / bestBucket.total,
        lon: bestBucket.lon / bestBucket.total,
      };
    }

    return null;
  }, [aggregatedCities]);

  const maxTotal = useMemo(() => {
    if (aggregatedCities.length === 0) return 1;
    return Math.max(...aggregatedCities.map((c) => c.total));
  }, [aggregatedCities]);

  const currentPointCityId = useMemo(() => {
    if (!currentPoint) return null;
    if (currentPoint.id && currentPoint.points) return currentPoint.id;

    const cityName = currentPoint.loc_data?.city || "Unknown";
    const country = currentPoint.country || "";
    return `${cityName}-${country}-${currentPoint.lat.toFixed(2)}-${currentPoint.lon.toFixed(2)}`;
  }, [currentPoint]);

  useEffect(() => {
    if (currentPointCityId) {
      const city = aggregatedCities.find((c) => c.id === currentPointCityId);
      setHoveredCity(null);
      setSelectedCity(city || null);
      setTimeout(() => setSelectedVisible(true), 50);
    } else {
      setSelectedVisible(false);
      setTimeout(() => setSelectedCity(null), 200);
    }
  }, [currentPointCityId, aggregatedCities]);

  useEffect(() => {
    if (!selectedCity || !globeEl.current) return;

    let animationFrameId;

    const updateTooltipPosition = () => {
      const coords = globeEl.current?.getScreenCoords(
        selectedCity.lat,
        selectedCity.lon,
        0.01,
      );
      if (coords) setTooltipPos({ x: coords.x, y: coords.y });
      animationFrameId = requestAnimationFrame(updateTooltipPosition);
    };

    updateTooltipPosition();
    return () => cancelAnimationFrame(animationFrameId);
  }, [selectedCity]);

  const getDotRadius = (cityData) => {
    const minRadius = 0.6;
    const maxRadius = 1.8;
    const scale = Math.sqrt(cityData.total) / Math.sqrt(maxTotal);
    const baseRadius = minRadius + scale * (maxRadius - minRadius);

    if (selectedCity?.id === cityData.id) return baseRadius * 1.25;
    if (hoveredCity?.id === cityData.id) return baseRadius * 1.15;
    return baseRadius;
  };

  const getLabelSize = (cityData) => {
    const minSize = 0.5;
    const maxSize = 1.4;
    const scale = Math.sqrt(cityData.total) / Math.sqrt(maxTotal);
    return minSize + scale * (maxSize - minSize);
  };

  const getCityColor = (cityData) => {
    if (selectedCity?.id === cityData.id) return "rgba(56, 189, 248, 1)";
    if (hoveredCity?.id === cityData.id) return "rgba(125, 211, 252, 1)";
    return "rgba(14, 165, 233, 0.9)";
  };

  useEffect(() => {
    if (globeEl.current && !hasInitialized.current) {
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = -0.3;

      if (mostConcentratedArea) {
        globeEl.current.pointOfView(
          {
            lat: mostConcentratedArea.lat,
            lng: mostConcentratedArea.lon,
            altitude: 2.5,
          },
          1000,
        );
        hasInitialized.current = true;
      } else if (aggregatedCities.length > 0) {
        const firstCity = aggregatedCities[0];
        globeEl.current.pointOfView(
          { lat: firstCity.lat, lng: firstCity.lon, altitude: 2.5 },
          1000,
        );
        hasInitialized.current = true;
      } else {
        globeEl.current.pointOfView({ altitude: 2.5 });
      }
    }
  }, [aggregatedCities, mostConcentratedArea]);

  useEffect(() => {
    if (
      selectedCity ||
      hoveredCity ||
      aggregatedCities.length === 0 ||
      !globeEl.current
    ) {
      setPassiveCity(null);
      setPassiveVisible(false);
      return;
    }

    let animationFrameId;
    let lastPassiveCityId = null;
    let tooltipTimeout = null;

    const findCenterlineCrossingCity = () => {
      if (!globeEl.current) return null;

      const centerX = dimensions.width / 2;
      const targetX = centerX - 30;
      const centerY = dimensions.height / 2;

      let bestCity = null;
      let bestScore = Infinity;

      for (const city of aggregatedCities) {
        const coords = globeEl.current?.getScreenCoords(
          city.lat,
          city.lon,
          0.01,
        );
        if (!coords) continue;

        const verticalDistance = Math.abs(coords.y - centerY);
        if (verticalDistance > dimensions.height * 0.3) continue;

        const horizontalDistance = Math.abs(coords.x - targetX);
        if (horizontalDistance > 80) continue;

        const score = horizontalDistance + verticalDistance * 0.5;
        if (score < bestScore) {
          bestScore = score;
          bestCity = city;
        }
      }

      return bestCity;
    };

    const updatePassiveTooltip = () => {
      const crossingCity = findCenterlineCrossingCity();

      if (crossingCity && crossingCity.id !== lastPassiveCityId) {
        lastPassiveCityId = crossingCity.id;
        setPassiveCity(crossingCity);
        setPassiveVisible(false);

        const coords = globeEl.current?.getScreenCoords(
          crossingCity.lat,
          crossingCity.lon,
          0.01,
        );
        if (coords) setPassiveTooltipPos({ x: coords.x, y: coords.y });

        setTimeout(() => setPassiveVisible(true), 50);
        if (tooltipTimeout) clearTimeout(tooltipTimeout);
        tooltipTimeout = setTimeout(() => setPassiveVisible(false), 4000);
      } else if (crossingCity?.id === lastPassiveCityId) {
        const coords = globeEl.current?.getScreenCoords(
          crossingCity.lat,
          crossingCity.lon,
          0.01,
        );
        if (coords) setPassiveTooltipPos({ x: coords.x, y: coords.y });
      } else if (!crossingCity && lastPassiveCityId) {
        setPassiveVisible(false);
        setTimeout(() => {
          if (!findCenterlineCrossingCity()) {
            setPassiveCity(null);
            lastPassiveCityId = null;
          }
        }, 300);
      }

      animationFrameId = requestAnimationFrame(updatePassiveTooltip);
    };

    const startDelay = setTimeout(() => updatePassiveTooltip(), 1000);

    return () => {
      clearTimeout(startDelay);
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      cancelAnimationFrame(animationFrameId);
    };
  }, [selectedCity, hoveredCity, aggregatedCities, dimensions]);

  useEffect(() => {
    if (globeEl.current && currentPoint) {
      const { lat, lon } = currentPoint;
      if (lat !== undefined && lon !== undefined) {
        globeEl.current.pointOfView({ lat, lng: lon, altitude: 1.5 }, 1500);
      }
    }
  }, [currentPoint]);

  const labelClickedRef = useRef(false);

  const stopAutoRotate = () => {
    if (!userHasInteracted.current && globeEl.current) {
      userHasInteracted.current = true;
      globeEl.current.controls().autoRotate = false;
    }
  };

  const handleCityClick = (cityData) => {
    stopAutoRotate();
    labelClickedRef.current = true;
    if (cityData?.points?.length > 0) {
      if (selectedCity?.id === cityData.id) {
        setHoveredCity(null);
        setSelectedCity(null);
        onPointClick(null);
        return;
      }
      setHoveredCity(null);
      setSelectedCity(cityData);
      onPointClick(cityData);
    }
  };

  const handleGlobeClick = () => {
    stopAutoRotate();
    if (labelClickedRef.current) {
      labelClickedRef.current = false;
      return;
    }
    setSelectedCity(null);
  };

  const renderSeverityItems = (d) => {
    const items = [];
    const severityConfig = [
      { key: "high", count: d.high, color: "#EF4444", label: "HIGH" },
      { key: "medium", count: d.medium, color: "#F59E0B", label: "MEDIUM" },
      { key: "low", count: d.low, color: "#10B981", label: "LOW" },
    ];

    severityConfig.forEach(({ key, count, color, label }) => {
      if (count > 0) {
        items.push(
          <div
            key={key}
            className="flex items-center justify-between px-3 py-2"
            style={{
              background: `${color}15`,
              borderLeft: `3px solid ${color}`,
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: color }}
              />
              <span className="text-xs" style={{ color }}>
                {label}
              </span>
            </div>
            <span className="text-slate-800 dark:text-gray-100 text-sm">
              {count}
            </span>
          </div>,
        );
      }
    });

    return items;
  };

  const ringsData = useMemo(() => {
    if (!selectedCity) return [];
    return [
      {
        lat: selectedCity.lat,
        lng: selectedCity.lon,
        maxR: 3,
        propagationSpeed: 2,
        repeatPeriod: 1000,
      },
    ];
  }, [selectedCity]);

  const tooltipStyle = (pos, visible) => ({
    position: "absolute",
    left: pos.x + TOOLTIP_OFFSET_X,
    top: pos.y + TOOLTIP_OFFSET_Y,
    pointerEvents: "none",
    opacity: visible ? 1 : 0,
    transform: visible
      ? "scale(1) translateY(0)"
      : "scale(0.9) translateY(8px)",
    transition: "opacity 0.2s ease-out, transform 0.2s ease-out",
  });

  return (
    <div id="globe-container" className="w-full h-full relative">
      <Globe
        ref={globeEl}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl={`//unpkg.com/three-globe/example/img/earth-${darkmode ? "night" : "day"}.jpg`}
        labelsData={aggregatedCities}
        labelLat={(d) => d.lat}
        labelLng={(d) => d.lon}
        labelText={(d) => `${d.city} (${d.total})`}
        labelSize={getLabelSize}
        labelDotRadius={getDotRadius}
        labelColor={getCityColor}
        labelResolution={6}
        labelAltitude={0.01}
        onLabelClick={handleCityClick}
        onLabelHover={(label) => {
          document.body.style.cursor = label ? "pointer" : "default";
          if (label && globeEl.current && !userHasInteracted.current) {
            globeEl.current.controls().autoRotate = false;
          } else if (!label && globeEl.current && !userHasInteracted.current) {
            globeEl.current.controls().autoRotate = true;
          }
          if (label && label.id !== selectedCity?.id) {
            setPassiveCity(null);
            setPassiveVisible(false);
            setHoveredCity(label);
            if (globeEl.current) {
              const coords = globeEl.current?.getScreenCoords(
                label.lat,
                label.lon,
                0.01,
              );
              if (coords) setHoverTooltipPos({ x: coords.x, y: coords.y });
            }
            setTimeout(() => setHoveredVisible(true), 50);
          } else {
            setHoveredVisible(false);
            setTimeout(() => setHoveredCity(null), 150);
          }
        }}
        onGlobeClick={handleGlobeClick}
        atmosphereColor="#0EA5E9"
        atmosphereAltitude={0.25}
        ringsData={ringsData}
        ringLat={(d) => d.lat}
        ringLng={(d) => d.lng}
        ringMaxRadius={(d) => d.maxR}
        ringPropagationSpeed={(d) => d.propagationSpeed}
        ringRepeatPeriod={(d) => d.repeatPeriod}
        ringColor={() => (t) => `rgba(14, 165, 233, ${1 - t})`}
        ringAltitude={0.01}
      />

      {selectedCity && (
        <div
          style={{ ...tooltipStyle(tooltipPos, selectedVisible), zIndex: 1000 }}
        >
          <TooltipContent
            city={selectedCity}
            renderSeverityItems={renderSeverityItems}
          />
        </div>
      )}

      {hoveredCity && (
        <div
          style={{
            ...tooltipStyle(hoverTooltipPos, hoveredVisible),
            zIndex: 1001,
          }}
        >
          <TooltipContent
            city={hoveredCity}
            renderSeverityItems={renderSeverityItems}
          />
        </div>
      )}

      {passiveCity && !selectedCity && !hoveredCity && (
        <div
          style={{
            ...tooltipStyle(passiveTooltipPos, passiveVisible),
            zIndex: 999,
            opacity: passiveVisible ? 0.9 : 0,
          }}
        >
          <TooltipContent
            city={passiveCity}
            renderSeverityItems={renderSeverityItems}
          />
        </div>
      )}
    </div>
  );
}

export default GlobeVisualization;
